import React from 'react';
import { Bot, MessageCircle, Zap, Repeat, Lightbulb, AlertTriangle, Cpu, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Link } from 'react-router-dom';
import BackButton from '../../../components/admin/BackButton';

/**
 * BMP ASSISTANT - OVERVIEW
 * Main dashboard for AI assistant features
 */

const AssistantOverview = () => {
  const features = [
    {
      title: "Chat",
      description: "Talk to your AI assistant for help and guidance",
      icon: MessageCircle,
      path: "/admin/assistant/chat",
      color: "bg-blue-500"
    },
    {
      title: "Actions",
      description: "Quick actions and automated workflows",
      icon: Zap,
      path: "/admin/assistant/actions",
      color: "bg-yellow-500"
    },
    {
      title: "Automations",
      description: "Set up automated tasks and routines",
      icon: Repeat,
      path: "/admin/assistant/automations",
      color: "bg-purple-500"
    },
    {
      title: "Insights",
      description: "AI-powered business insights and analytics",
      icon: Lightbulb,
      path: "/admin/assistant/insights",
      color: "bg-orange-500"
    },
    {
      title: "Alerts",
      description: "Smart notifications and warnings",
      icon: AlertTriangle,
      path: "/admin/assistant/alerts",
      color: "bg-red-500"
    },
    {
      title: "System Intelligence",
      description: "Platform optimization and intelligence",
      icon: Cpu,
      path: "/admin/assistant/system",
      color: "bg-green-500"
    }
  ];

  const stats = [
    { label: "Active Automations", value: "0", icon: Repeat },
    { label: "Insights Generated", value: "0", icon: Lightbulb },
    { label: "Actions Performed", value: "0", icon: Zap },
    { label: "Time Saved", value: "0h", icon: TrendingUp }
  ];

  return (
    <div>
      <BackButton />
      
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
            <Bot className="h-8 w-8 text-[#00B4D8]" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-[#2B2D42]">BMP Assistant</h2>
            <p className="text-[#6C757D] mt-1">
              Your AI-powered personal assistant for platform management
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#6C757D] mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-[#2B2D42]">{stat.value}</p>
                  </div>
                  <div className="p-3 bg-[#E0F2FE] rounded-lg">
                    <Icon className="h-6 w-6 text-[#00B4D8]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Start */}
      <Card className="mb-8 border-[#00B4D8] bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Bot className="h-8 w-8 text-[#00B4D8]" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-[#2B2D42] mb-2">
                Welcome to BMP Assistant!
              </h3>
              <p className="text-[#6C757D] mb-4">
                Your intelligent assistant is ready to help you manage the platform more efficiently. 
                Get started by exploring the features below or chat directly with the assistant.
              </p>
              <Link to="/admin/assistant/chat">
                <Button className="bg-[#00B4D8] hover:bg-[#0096B8]">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Start Chatting
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div>
        <h3 className="text-xl font-bold text-[#2B2D42] mb-4">Assistant Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Link key={index} to={feature.path} className="group">
                <Card className="hover:shadow-lg hover:border-[#00B4D8] transition-all h-full">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`${feature.color} p-3 rounded-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <CardTitle className="group-hover:text-[#00B4D8] transition-colors">
                      {feature.title}
                    </CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Coming Soon Notice */}
      <Card className="mt-8 bg-yellow-50 border-yellow-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Lightbulb className="h-6 w-6 text-yellow-600 mt-1" />
            <div>
              <h4 className="font-semibold text-[#2B2D42] mb-1">AI Features Coming Soon</h4>
              <p className="text-sm text-[#6C757D]">
                The BMP Assistant is currently in development. Full AI capabilities, including natural language processing, 
                automated workflows, and intelligent insights will be available in the next release.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssistantOverview;